/**
 * 通用邮件发送工具
 * 用于封装 Contact Us 和 Become Supplier 表单的邮件发送逻辑
 */

import {
  DEFAULT_LANG_ID,
  DEFAULT_SHOW_ID,
  DEFAULT_SHOW_CODE,
  DEFAULT_SUBMIT_PAGE_URL,
  CAPTCHA_CONFIG,
  getCurrentLangId
} from './new-login-config.js';
import request from '../../../_helpers/request';

/**
 * 构建基础 FormData（所有表单共用的字段）
 * @param {Object} formData - 表单数据
 * @param {Object} captchaData - 验证码数据
 * @returns {FormData} FormData 对象
 */
function buildBaseFormData(formData, captchaData) {
  const formDataToSend = new FormData();
  const currentLangId = formData.langid || getCurrentLangId();
  
  // 基础字段
  formDataToSend.append('fid', formData.fid || '');
  formDataToSend.append('showid', formData.showid || DEFAULT_SHOW_ID);
  formDataToSend.append('showcode', formData.showcode || DEFAULT_SHOW_CODE);
  formDataToSend.append('langid', currentLangId);
  formDataToSend.append('submitPageURL', formData.submitPageURL || DEFAULT_SUBMIT_PAGE_URL);
  formDataToSend.append('IsNewValidCode', formData.IsNewValidCode || CAPTCHA_CONFIG.IsNewValidCode);
  formDataToSend.append('isNotNeedValidate', formData.isNotNeedValidate || CAPTCHA_CONFIG.isNotNeedValidate);
  formDataToSend.append('hid_codepage', formData.hidCodepage || String(currentLangId));
  
  // 验证码相关字段
  formDataToSend.append('ValType', CAPTCHA_CONFIG.ValType);
  formDataToSend.append('isCloud', CAPTCHA_CONFIG.isCloud);
  formDataToSend.append('IsAliyunValid', CAPTCHA_CONFIG.IsAliyunValid);
  formDataToSend.append('RefererLink', window.location.href || CAPTCHA_CONFIG.RefererLink);
  formDataToSend.append('typename', CAPTCHA_CONFIG.typename);
  
  if (captchaData) {
    formDataToSend.append('captcha_output', captchaData.captcha_output || '');
    formDataToSend.append('gen_time', captchaData.gen_time || '');
    formDataToSend.append('lot_number', captchaData.lot_number || '');
    formDataToSend.append('pass_token', captchaData.pass_token || '');
  }
  
  if (formData.captchaVerifyParam) {
    formDataToSend.append('captchaVerifyParam', formData.captchaVerifyParam);
  }
  
  // 添加 captcha_ticket 和 captcha_randstr（用于腾讯验证码）
  if (formData.captcha_ticket) {
    formDataToSend.append('captcha_ticket', formData.captcha_ticket);
  }
  if (formData.captcha_randstr) {
    formDataToSend.append('captcha_randstr', formData.captcha_randstr);
  }
  
  return formDataToSend;
}

/**
 * 根据联系原因（Reason）映射到邮件项目类型（EmailProject）
 * 严格参考前端指导文件的映射规则
 * @param {number|string} reason - 联系原因值
 * @returns {string} EmailProject值
 */
export function getEmailProjectByReason(reason) {
  const reasonValue = parseInt(reason);
  const emailProjectMap = {
    2: 'Enq_attending',      // 参观查询
    3: 'Enq_exhibiting',     // 参展查询
    4: 'Enq_partnership',    // 合作查询
    5: 'Enq_web_advertising', // 网络广告
    6: 'Enq_others',         // 其他查询
    7: 'Enq_others'          // 其他查询
  };
  
  // 如果无法映射，默认使用 Enq_others
  return emailProjectMap[reasonValue] || 'Enq_others';
}

/**
 * 从数据库获取邮件配置
 * 根据 ShowID、LangID 和 EmailProject 从数据库读取邮件配置
 * @param {number} showId - 展会ID
 * @param {number} langId - 语言ID
 * @param {string} emailProject - 邮件项目类型
 * @returns {Promise<Object>} 邮件配置对象
 */
export async function getEmailConfigFromDatabase(showId, langId, emailProject, showCode = '') {
  try {
    // 调用后端API获取邮件配置
    // API路径：EMPBackend/GetEmailConfig (POST方法)
    // 后端方法签名：GetEmailConfig(GetWebConfigParam param)
    // GetWebConfigParam 包含：wcParam, showid, showcode, isRefresh
    // 注意：根据后端实现，可能需要调整 wcParam 的格式
    const params = {
      wcParam: emailProject, // 邮件项目类型，可能需要结合 langId，格式待确认
      showid: showId,
      showcode: showCode || '',
      isRefresh: false,
      langId: langId // 如果后端需要单独传递 langId
    };
    
    const response = await request.post('EMPBackend/GetEmailConfig', params);
    
    // request 函数已经处理了响应拦截，直接返回 res.data
    if (response && response.success && response.data) {
      return {
        FromEmail: response.data.FromEmail || 'emarketplace@adsale.com.hk',
        FromName: response.data.FromName || '',
        ReplyTo: response.data.ReplyTo || '',
        BCC: response.data.BCC || '',
        CC: response.data.CC || '',
        BCC_Test: response.data.BCC_Test || '',
        CC_Test: response.data.CC_Test || '',
        Title: response.data.Title || 'eMarketplace - Contact Us',
        TO: response.data.TO || ''
      };
    } else {
      // 如果数据库没有配置，返回默认值
      return getDefaultEmailConfig();
    }
  } catch (error) {
    console.warn('获取邮件配置失败，使用默认配置:', error);
    // 如果API调用失败，返回默认配置
    return getDefaultEmailConfig();
  }
}

/**
 * 获取默认邮件配置（当数据库没有配置时使用）
 * @returns {Object} 默认邮件配置
 */
function getDefaultEmailConfig() {
  return {
    FromEmail: 'emarketplace@adsale.com.hk',
    FromName: '',
    ReplyTo: '',
    BCC: '',
    CC: '',
    BCC_Test: '',
    CC_Test: '',
    Title: 'eMarketplace - Contact Us',
    TO: ''
  };
}

/**
 * 构建 Contact Us 表单的 FormData
 * @param {Object} formData - Contact Us 表单数据
 * @param {Object} captchaData - 验证码数据
 * @param {Object} emailConfig - 邮件配置（从数据库获取）
 * @returns {FormData} FormData 对象
 */
export function buildContactUsPayload(formData, captchaData, emailConfig) {
  const formDataToSend = buildBaseFormData(formData, captchaData);
  
  // 邮件配置（严格参考前端指导文件）
  // TO（收件人）= 用户填写的邮箱（发送确认邮件）
  // BCC/CC（密送/抄送）= 数据库配置的内部人员邮箱（发送通知邮件）
  formDataToSend.append('email_to', formData.Email || ''); // 用户邮箱 - 发送确认邮件
  formDataToSend.append('email_from', emailConfig.email_from || emailConfig.FromEmail || 'emarketplace@adsale.com.hk');
  formDataToSend.append('email_fromName', emailConfig.email_fromName || emailConfig.FromName || '');
  formDataToSend.append('email_replyTo', emailConfig.email_replyTo || emailConfig.ReplyTo || '');
  formDataToSend.append('email_bcc', emailConfig.email_bcc || emailConfig.BCC || '');
  formDataToSend.append('email_cc', emailConfig.email_cc || emailConfig.CC || '');
  formDataToSend.append('email_subject', emailConfig.email_subject || emailConfig.Title || 'eMarketplace - Contact Us');
  formDataToSend.append('email_confirm_message', emailConfig.email_confirm_message || 'Send form Success');
  
  // Contact Us 表单字段
  formDataToSend.append('Reason', formData.Reason || '');
  formDataToSend.append('ReasonText', formData.ReasonText || '');
  formDataToSend.append('message', formData.message || '');
  formDataToSend.append('name', formData.name || '');
  formDataToSend.append('Company', formData.Company || '');
  formDataToSend.append('Country', formData.Country || '');
  formDataToSend.append('CountryText', formData.CountryText || '');
  formDataToSend.append('Mobile', formData.Mobile || '');
  formDataToSend.append('Email', formData.Email || '');
  formDataToSend.append('hdPrivacy', formData.hdPrivacy ? 'true' : 'false');
  formDataToSend.append('hdShare', formData.hdShare ? 'true' : 'false');
  formDataToSend.append('chkPrivacy', formData.hdPrivacy ? 'true' : 'false');
  formDataToSend.append('chkShare', formData.hdShare ? 'true' : 'false');
  
  const user = localStorage.getItem('user');
  if (user) {
    try {
      const userData = JSON.parse(user);
      if (userData && userData.empCompanyId) {
        formDataToSend.append('IsNeedVerify', 'false');
      }
    } catch (e) {
      // ignore
    }
  }
  
  return formDataToSend;
}

/**
 * 构建 Become Supplier 表单的 FormData
 * @param {Object} formData - Become Supplier 表单数据
 * @param {Object} captchaData - 验证码数据
 * @param {Object} emailConfig - 邮件配置
 * @returns {FormData} FormData 对象
 */
export function buildBecomeSupplierPayload(formData, captchaData, emailConfig) {
  const formDataToSend = buildBaseFormData(formData, captchaData);
  
  // 邮件配置
  formDataToSend.append('email_to', emailConfig.email_to);
  formDataToSend.append('email_from', emailConfig.email_from);
  formDataToSend.append('email_subject', emailConfig.email_subject);
  formDataToSend.append('email_confirm_message', emailConfig.email_confirm_message || 'Send form Success');
  
  // Become Supplier 表单字段
  formDataToSend.append('message', formData.messages || '');
  formDataToSend.append('name', formData.fullName || '');
  formDataToSend.append('Company', formData.companyName || '');
  formDataToSend.append('Email', formData.businessEmail || '');
  formDataToSend.append('Country', formData.country || '');
  formDataToSend.append('CountryText', getCountryText(formData.country, formData.countryOptions) || '');
  formDataToSend.append('Mobile', formData.mobileNumber || '');
  formDataToSend.append('Reason', formData.hearAboutUs || '');
  formDataToSend.append('ReasonText', getHearAboutUsText(formData.hearAboutUs) || '');
  
  return formDataToSend;
}

/**
 * 发送表单邮件
 * @param {Object} config - 配置对象
 * @param {Object} config.formData - 表单数据
 * @param {Object} config.captchaData - 验证码数据（Geetest）
 * @param {Object} config.emailConfig - 邮件配置
 * @param {string} config.emailConfig.email_to - 收件人邮箱
 * @param {string} config.emailConfig.email_from - 发件人邮箱
 * @param {string} config.emailConfig.email_subject - 邮件标题
 * @param {string} config.emailConfig.email_confirm_message - 确认消息
 * @param {string} config.formType - 表单类型 ('contact-us' | 'become-supplier')
 * @returns {Promise<Object>} 返回提交结果
 */
export async function sendFormEmail(config) {
  const { formData, captchaData, emailConfig, formType } = config;
  
  // 根据表单类型构建 FormData
  let formDataToSend;
  if (formType === 'contact-us') {
    formDataToSend = buildContactUsPayload(formData, captchaData, emailConfig);
  } else if (formType === 'become-supplier') {
    formDataToSend = buildBecomeSupplierPayload(formData, captchaData, emailConfig);
  } else {
    throw new Error(`Unknown form type: ${formType}`);
  }
  
  try {
    let body;
    if (formDataToSend instanceof FormData) {
      const params = new URLSearchParams();
      for (const [key, value] of formDataToSend.entries()) {
        params.append(key, value);
      }
      body = params.toString();
    } else {
      body = new URLSearchParams(formDataToSend).toString();
    }
    
    const response = await fetch('https://e.adsale.com.hk/ReplyCoupon/ReplyCoupon/ToDoSubmit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8'
      },
      body: body
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Network response was not ok: ${response.status} ${errorText}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('sendFormEmail error:', error);
    throw error;
  }
}

/**
 * 已弃用：通用邮件配置常量
 * 现在邮件配置从数据库动态获取，不再使用硬编码的常量
 * 保留此常量仅用于向后兼容，新代码应该使用 getEmailConfigFromDatabase 函数
 * @deprecated 请使用 getEmailConfigFromDatabase 函数从数据库获取邮件配置
 */
export const CONTACT_US_EMAIL_CONFIG = {
  email_to: 'emarketplace@adsale.com.hk, eis@adsale.com.hk, buyer@adsale.com.hk',
  email_from: 'emarketplace@adsale.com.hk',
  email_subject: 'eMarketplace - Contact Us (Supplier)',
  email_confirm_message: 'Send form Success'
};

/**
 * 已弃用：通用邮件配置常量
 * 现在邮件配置从数据库动态获取，不再使用硬编码的常量
 * @deprecated 请使用 getEmailConfigFromDatabase 函数从数据库获取邮件配置
 */
export const BECOME_SUPPLIER_EMAIL_CONFIG = {
  email_to: 'emarketplace@adsale.com.hk, eis@adsale.com.hk, buyer@adsale.com.hk',
  email_from: 'emarketplace@adsale.com.hk',
  email_subject: 'eMarketplace - Contact Us (Become our supplier)',
  email_confirm_message: 'Send form Success'
};


/**
 * 获取 "How did you hear about us?" 选项的文本
 * @param {string} value - 选项值
 * @returns {string} 选项文本
 */
function getHearAboutUsText(value) {
  const options = {
    'search-engine': 'Search engine (Google, Bing, etc.)',
    'social-media': 'Social media (Facebook, LinkedIn, Instagram, etc.)',
    'online-advertisement': 'Online advertisement',
    'email-newsletter': 'Email newsletter',
    'trade-show': 'Trade show / Exhibition',
    'webinar': 'Webinar / Online event',
    'recommendation': 'Recommendation from colleague / friend',
    'invited': 'Invited by buyer / supplier partner',
    'business-association': 'Business association / Chamber of Commerce',
    'cps-campaign': 'CPS+ eMarketplace campaign',
    'company-website': 'Company / Adsale Group website',
    'other': 'Other (please specify)'
  };
  
  return options[value] || value;
}

/**
 * 根据国家代码获取国家文本
 * @param {string} countryCode - 国家代码
 * @param {Array} countryOptions - 国家选项列表
 * @returns {string} 国家文本
 */
function getCountryText(countryCode, countryOptions) {
  if (!countryCode || !countryOptions || countryOptions.length === 0) {
    return countryCode || '';
  }
  
  const country = countryOptions.find(opt => opt.value === countryCode);
  return country ? country.text : countryCode;
}

/**
 * 获取 "How did you hear about us?" 选项列表
 * @returns {Array} 选项列表
 */
export function getHearAboutUsOptions() {
  return [
    { value: '', text: 'Select an option' },
    { value: 'search-engine', text: 'Search engine (Google, Bing, etc.)' },
    { value: 'social-media', text: 'Social media (Facebook, LinkedIn, Instagram, etc.)' },
    { value: 'online-advertisement', text: 'Online advertisement' },
    { value: 'email-newsletter', text: 'Email newsletter' },
    { value: 'trade-show', text: 'Trade show / Exhibition' },
    { value: 'webinar', text: 'Webinar / Online event' },
    { value: 'recommendation', text: 'Recommendation from colleague / friend' },
    { value: 'invited', text: 'Invited by buyer / supplier partner' },
    { value: 'business-association', text: 'Business association / Chamber of Commerce' },
    { value: 'cps-campaign', text: 'CPS+ eMarketplace campaign' },
    { value: 'company-website', text: 'Company / Adsale Group website' },
    { value: 'other', text: 'Other (please specify)' }
  ];
}

